import React, { ReactElement } from "react";
import { BiBlock } from "react-icons/bi";
import {
    HiOutlineChevronDoubleDown, HiOutlineChevronDoubleUp,
} from "react-icons/hi";
import { IoReorderTwoOutline } from "react-icons/io5";
import { MdOutlineMarkChatUnread } from "react-icons/md";
import { RiErrorWarningLine } from "react-icons/ri";
import TimeAgo from "react-timeago";
import useRouting, { Link } from "../../../contexts/Routing";
import useSelectionState, { SelectionState, SelectionType, Ticket } from "../../../pages/agent/selection-state";
import {
    ChatStatus, DashboardGroupBy, DashboardView, DueDateType, TicketPriority, TicketView,
} from "../../../__generated__/api";
import Avatar from "../../avatar/Avatar";
import Styles from "./Card.module.scss";
import usePortal from "../../../contexts/PortalContext";

interface CardProps {
    value: Ticket;
    view: DashboardView
    groupBy: DashboardGroupBy;
    className?: string;
    preview?: ReactElement;
    searchParams?: Record<string, string>;
    registerCardPrefix?: string;
    ticketView: TicketView;
    hasSlas: boolean
}

export const PRIORITY_TEXT = {
    [TicketPriority.Critical]: "Critical",
    [TicketPriority.High]: "High",
    [TicketPriority.Medium]: "Medium",
    [TicketPriority.Low]: "Low",
};

export const PRIORITY_ICON_IMAGE = {
    [TicketPriority.Critical]: (
        <BiBlock className="text-danger " title="Critical Priority" />
    ),
    [TicketPriority.High]: (
        <HiOutlineChevronDoubleUp className="text-danger-light" title="High Priority" />
    ),
    [TicketPriority.Medium]: (
        <IoReorderTwoOutline title="Medium Priority" />
    ),
    [TicketPriority.Low]: (
        <HiOutlineChevronDoubleDown className="text-light " title="Low Priority" />
    ),
};
export const PRIORITY_ICON = {
    [TicketPriority.Critical]: (
        <div className="d-flex align-items-center">
            <BiBlock className="me-1 text-danger " />
            {" "}
            Critical
        </div>
    ),
    [TicketPriority.High]: (
        <div className="d-flex align-items-center">
            <HiOutlineChevronDoubleUp className="me-1 text-warning" />
            {" "}
            High
        </div>
    ),
    [TicketPriority.Medium]: (
        <div className="d-flex align-items-center">
            <IoReorderTwoOutline className="me-1 text-primary" />
            {" "}
            Medium
        </div>
    ),
    [TicketPriority.Low]: (
        <div className="d-flex align-items-center">
            <HiOutlineChevronDoubleDown className="me-1 text-light" />
            {" "}
            Low
        </div>
    ),

};

const SLA_TEXTS = {
    [DueDateType.SlaFirstResponse]: "1st Response",
    [DueDateType.SlaTimeToUpdate]: "Update Time",
    [DueDateType.SlaRequesterWaitTime]: "Requester Wait time",
};

const STATUS_TEXT = {
    [ChatStatus.Assigned]: "Assigned",
    [ChatStatus.ChatRequested]: "Waiting",
    [ChatStatus.Connected]: "Connected",
    [ChatStatus.Finished]: "Finished",
    [ChatStatus.Ignored]: "Ignored",
    [ChatStatus.OnHold]: "On Hold",
    [ChatStatus.Snoozed]: "Snoozed",
    [ChatStatus.DeflectionWaiting]: "Deflection Waiting",
};

export const FEATURE_DARKER = "frontend.design.darker";

export default function Card({
    value, view, groupBy, className, preview, searchParams, registerCardPrefix, ticketView, hasSlas,
}: CardProps): ReactElement | null {
    const { Paths } = useRouting();
    const selectionState = useSelectionState();
    const { featureFlags } = usePortal();
    const {
        selection, ref, version, isUnread,
    } = selectionState.registerCard(registerCardPrefix + value.id);
    const currentChat = selectionState.getChat(value.id);
    const isCompact = view === DashboardView.Compact;
    if (selection === SelectionType.HIDDEN) {
        return null;
    }
    const modifiedDate = new Date(value.lastUserMessageDate ?? value.modifiedDate);
    const msg = preview ?? currentChat?.lastMessage ?? (value.status === ChatStatus.OnHold ? value.holdNotes : value.lastMessage);
    const textClass = selection === SelectionType.NOTIFICATION ? "text-gray-100" : "";

    const context = [];

    const isDarkerFeature = featureFlags.has(FEATURE_DARKER);

    if (isCompact) {
        context.push(
            <span className={selection === SelectionType.NOTIFICATION ? "text-light" : "text-primary"}>
                {PRIORITY_ICON_IMAGE[value.priority ?? TicketPriority.Medium]}
            </span>,
        );
    } else if (!isDarkerFeature) {
        context.push(PRIORITY_TEXT[value.priority ?? TicketPriority.Medium]);
    }

    if (isCompact && groupBy !== DashboardGroupBy.User) {
        context.push(
            <span
            // href={value.source.url as string}
                className="text-gray-500 text-decoration-none d-flex align-items-center"
                title="Requester"
            >
                <Avatar src={value.creator.avatarUrl as string} variant="xxs" />
                <span>
                    {value.creator?.name}
                </span>

            </span>,
        );
    }

    if (groupBy !== DashboardGroupBy.Assignee && ticketView !== TicketView.MyTickets) {
        if (!isCompact && value.isAssignedToCurrentUser) {
            context.push(STATUS_TEXT[value.status ?? ChatStatus.ChatRequested]);
        } else if (!value.isAssignedToCurrentUser && value.assignee) {
            context.push(
                <span
                    className="text-gray-500 text-decoration-none d-flex align-items-center"
                >

                    <Avatar src={value.assignee.avatarUrl as string} variant="xxs" />
                    <span>
                        {value.assignee?.name}
                        {" "}
                        (
                        {STATUS_TEXT[value.status ?? ChatStatus.ChatRequested]}
                        )
                    </span>
                </span>,
            );
        } else if (isCompact) {
            context.push(<span>You</span>);
        }
    }

    if (value.source) {
        context.push(
            <span
                // href={value.source.url as string}
                className="text-gray-500 text-decoration-none"
                // target="_blank"
                // rel="noreferrer"
            >
                {value.source.text}
            </span>,
        );
    } else if (isCompact) {
        context.push(<span />);
    }

    let pastSlaMessage: string | undefined;
    if (value.ticket?.dueDates) {
        const latest = value.ticket?.dueDates
            .filter(row => !row.completedDate)
            .min(undefined, row => (row.dueDate ? new Date(row.dueDate)?.getTime() : undefined) ?? Number.MAX_SAFE_INTEGER);
        if (latest) {
            const dueDate = new Date(latest.dueDate);
            // const startDate = new Date(latest.startedDate);
            // const slaTime = prettyMilliseconds(dueDate.getTime() - startDate.getTime(), { verbose: true });

            const isPastSla = dueDate.getTime() < Date.now();
            pastSlaMessage = isPastSla ? `${SLA_TEXTS[latest.type]}` : undefined;

            if (!isCompact) {
                if (isPastSla) {
                    context.unshift(
                        <small className={isPastSla ? "text-danger " : ""} title={pastSlaMessage}>
                            <span
                                className="p-1 me-1 bg-danger-light badge badge-pill rounded"
                            >
                                <RiErrorWarningLine />
                            </span>
                            <span className="align-bottom">
                                {pastSlaMessage}
                            </span>
                        </small>,
                    );
                } else if (!isCompact) {
                    if (isDarkerFeature) {
                        context.unshift(
                            <small title={pastSlaMessage}>
                                <span
                                    className="text-gray-600 me-1 align-bottom"
                                >
                                    <RiErrorWarningLine />
                                </span>
                                <span className="text-primary">
                                    <TimeAgo date={dueDate} />
                                </span>
                            </small>,
                        );
                    } else {
                        context.push(
                            <span className="badge bg-light ">
                                <TimeAgo date={dueDate} />
                            </span>,
                        );
                    }
                }
            }
        }
    }

    const clicked = (event: React.MouseEvent) => {
        if (event.defaultPrevented) {
            return;
        }
        selectionState.toggleSelection(value.id);

        if (event.metaKey) {
            event.preventDefault();
        }
    };

    const classNames = [
        "rounded-0 rounded-start p-2 ms-2 mt-1 ",
        Styles["ticket-item"],
        Styles[`ticket-item-${(value.priority ?? TicketPriority.Medium)?.toLowerCase()}`],
    ];
    if (isCompact && isDarkerFeature) {
        classNames.push(`mb-0 mt-0 ${Styles["ticket-border-bottom-darker"]}`);
    } else {
        classNames.push("mb-2");
    }
    if (className) {
        classNames.push(...className.split(" "));
    }
    if (selection === SelectionType.NOTIFICATION) {
        classNames.push(Styles["card-highlight"], Styles["card-highlight-new"]);
    } else if (selection === SelectionType.SELECTED) {
        classNames.push(Styles["card-highlight-selected"]);
    } else if (classNames.every(name => !name?.startsWith("bg-"))) {
        classNames.push("bg-white");
    }
    classNames.push("list-group-item list-group-item-action border-top-0 border-end-0");

    const timer = (
        <TimeAgo
            date={modifiedDate}
            minPeriod={60}
            title={`${modifiedDate.toLocaleDateString()} ${modifiedDate.toLocaleTimeString()}`}
        />
    );
    if (isCompact) {
        context.push(timer);
        classNames.push("d-flex justify-content-between");
    }

    return (
        <Link
            to={Paths.AGENT_CHAT}
            params={{ chatId: value.ticketNumber?.toString() ?? value.id as string }}
            className={classNames.join(" ")}
            search={searchParams}
            onClick={clicked}
            aria-current="true"
            linkRef={ref}
            key={`${value.id}-${version}`}
        >
            <div className="d-flex justify-content-between  align-items-center w-100">

                {isCompact && value.ticketNumber && (
                    <>
                        {isUnread ? (
                            <span className="p-1 ms-n1 bg-danger badge badge-pill rounded me-2 shadow" title="Unread messages">
                                <MdOutlineMarkChatUnread />
                                <span className="visually-hidden">New unreads</span>
                            </span>
                        ) : (
                            <span className="ms-4" />
                        )}
                        {hasSlas && (
                            pastSlaMessage ? (
                                <span
                                    className="p-1 ms-n1 bg-danger-light badge badge-pill rounded me-2 shadow"
                                    title={pastSlaMessage}
                                >
                                    <RiErrorWarningLine />
                                    <span className="visually-hidden">{pastSlaMessage}</span>
                                </span>
                            ) : (
                                <span className="ms-4" />
                            )
                        )}

                        <h6 className="text-light me-3 mb-0">
                            #
                            {value.ticketNumber}
                        </h6>
                    </>
                )}
                {isCompact ? (
                    <h6 className={`col mb-0 ${textClass}`}>
                        {value.subject}
                    </h6>
                ) : (
                    <div className="d-flex align-items-center">
                        {isUnread && (
                            <span className="p-1 bg-danger badge badge-pill rounded me-2 shadow" title="Unread messages">
                                <MdOutlineMarkChatUnread />
                                <span className="visually-hidden">New unreads</span>
                            </span>
                        )}
                        <h6 className="mb-1 d-inline">
                            {value.ticketNumber && (
                                <small className="text-light me-2">
                                    #
                                    {value.ticketNumber}
                                </small>
                            )}
                            {value.subject}
                        </h6>
                    </div>
                )}

                {view !== DashboardView.Compact && (
                    <small className="text-gray-800">
                        {timer}
                    </small>
                )}

            </div>

            <p className={` fs-6 mt-2 d-flex align-items-center flex-wrap mt-0 fw-light
                            text-gray-500 mb-0 flex-shrink-0 ${isCompact ? "col-8" : ""}`}
            >
                {isCompact && (
                    <small className="col-4 text-gray-500 text-truncate">
                        {msg !== value.subject ? msg : ""}
                    </small>
                )}
                {context.map((row, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <small key={`row-${index}`} className={isCompact ? "col text-start px-1" : " d-flex align-items-center"}>
                        {row}
                        {(index !== context.length - 1 && !isCompact) && <span className="mx-2 text-gray-400">|</span>}
                    </small>
                ))}

            </p>

        </Link>

    );
}

Card.defaultProps = {
    className: "",
    preview: undefined,
    searchParams: undefined,
    registerCardPrefix: "",
};

export interface CardCloneProps {
    selectionState: SelectionState;
    view: DashboardView;
    groupBy: DashboardGroupBy;
    ticketView: TicketView
}

export function CardClone({
    selectionState, view, groupBy, ticketView,
}: CardCloneProps): ReactElement | null {
    const selection = selectionState.getSelection();
    if (selection.isEmpty) {
        return null;
    }
    if (selection.length === 1) {
        return (
            <Card
                value={selection.firstElement}
                view={view}
                groupBy={groupBy}
                ticketView={ticketView}
                hasSlas={false}
                className="border"
            />
        );
    }

    const classNames = [
        "rounded-0 rounded-start ",
        "bg-white",
        Styles["ticket-item-clone"],
        "list-group-item list-group-item-action border-top",
    ].join(" ");
    return (
        <div>

            {selection.length > 2 && (
                <div className={`${classNames}`} />
            )}
            <div className={`${classNames} ${Styles["ticket-item-clone-back"]}`} />
            <div className={`${Styles["ticket-item-clone-front"]}`}>
                <Card
                    value={selection.firstElement}
                    view={view}
                    groupBy={groupBy}
                    ticketView={ticketView}
                    hasSlas={false}
                    className="border"
                />
            </div>
            <div className={`d-flex justify-content-end ${Styles["ticket-item-clone-count"]}`}>
                <div className={`badge rounded-pill text-bg-blue-dark fs-4 ${Styles["ticket-item-clone-count-label"]}`}>
                    {selection.length}
                </div>
            </div>

        </div>

    );
}
